import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Shield, Award, MapPin, BadgeCheck, Flag, Sparkles,
  Home as HomeIcon, Building2, Users, Building, HardHat, Globe2, Clock, Compass, ArrowRight, Calculator,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchBar from '../components/SearchBar';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import HabitaValueProposition from '../components/HabitaValueProposition';
import { PropertyCardSkeletonGrid } from '../components/Skeletons';
import { POPULAR_AREAS } from '../mock';
import { api } from '../lib/api';

// City hero images kept as-is (they're stock photos, not fake data).
// Fake property counts REMOVED — cards now show only image + name + CTA.

/* Optional: only render sections that actually have real data. When the DB
 * is empty (dev / first install), we fall back to demo listings and hide
 * the social-proof + recent sections entirely. */

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  const [news, setNews] = useState([]);
  const [loadingFeat, setLoadingFeat] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    api.getFeatured(9)
      .then(r => setFeatured(r.items || []))
      .catch(() => {})
      .finally(() => setLoadingFeat(false));
    api.getRecent(8)
      .then(r => setRecent(r.items || []))
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
    api.getPlatformStats()
      .then(r => setStats(r.stats || null))
      .catch(() => {});
    api.listNews()
      .then(r => setNews((r.items || []).slice(0, 4)))
      .catch(() => {});
  }, []);

  const hasRealFeatured = featured.length > 0;
  const hasRealRecent = recent.length > 0;
  const hasStats = stats && Object.keys(stats).length > 0;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Encuentra tu próximo hogar en Colombia"
        description="Compra, arrienda o publica propiedades en cualquiera de los 1.122 municipios de Colombia. La plataforma inmobiliaria de cobertura nacional para colombianos."
      />
      <Header />

      {/* HERO */}
      <section className="relative bg-habita-cream overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #00824D 0%, transparent 50%), radial-gradient(circle at 80% 70%, #d97706 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-habita-mint text-habita rounded-full text-xs font-semibold tracking-wide">
                <Sparkles className="w-3.5 h-3.5" /> BETA · INVENTARIO CURADO Y VERIFICADO
              </span>
              <h1 className="font-serif text-5xl lg:text-7xl font-bold leading-[1.05] mt-5 text-habita-dark">
                Encuentra tu <span className="italic text-amber-600">próximo</span> hogar.
              </h1>
              <p className="mt-5 text-lg text-gray-600 max-w-lg leading-relaxed">
                Propiedades verificadas, agentes certificados y precios reales. Habita es la nueva forma de comprar y arrendar en Colombia.
              </p>
              <span className="lg:hidden inline-flex items-center gap-1.5 mt-5 px-3 py-1.5 rounded-full bg-habita text-white text-xs font-semibold" data-testid="home-beta-badge-mobile">
                🇨🇴 Cobertura nacional · 1.122 municipios
              </span>
              <div className="mt-8">
                <SearchBar />
              </div>

              {/* SEGUNDO CAMINO — Habita Advisor. Explica en segundos qué hace
                  y diferencia del buscador. Más presencia que la valoración.
                  CTA abre el flujo existente /advisor/inicio (sin login). */}
              <div
                data-testid="home-advisor-block"
                className="mt-6 rounded-2xl bg-habita-dark p-5 text-white shadow-lg shadow-habita-dark/10"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <Compass className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">Habita Advisor</p>
                    <h3 className="font-serif text-xl font-bold leading-tight mt-0.5">¿No sabes por dónde empezar?</h3>
                    <p className="text-sm text-white/80 mt-1 max-w-md">
                      Habita Advisor te ayuda a encontrar las opciones que realmente encajan contigo.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {['Te hace preguntas', 'Entiende tus preferencias', 'Compara y decides mejor'].map((t) => (
                        <span key={t} className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white/90">{t}</span>
                      ))}
                    </div>
                    <Link
                      to="/advisor/inicio"
                      data-testid="home-advisor-cta"
                      className="group mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-habita-dark text-sm font-bold hover:bg-habita-mint transition-colors"
                    >
                      Empezar con Habita Advisor
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* TERCER CAMINO — Valoración para propietarios. Simulación (no
                  promesa exacta), contempla venta Y arriendo. Ruta sin cambios. */}
              <Link
                to="/valorar-gratis"
                data-testid="home-valorar-cta"
                className="group mt-4 block rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-4 hover:bg-emerald-100 hover:shadow-lg hover:shadow-emerald-600/10 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-600">
                    <Calculator className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-emerald-900 sm:text-base leading-tight">
                      ¿Cuánto podrías vender o arrendar tu inmueble?
                    </p>
                    <p className="text-xs text-emerald-800/80 mt-0.5 font-medium">
                      Simula su valor en pocos minutos, gratis y sin compromiso.
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-800">
                      Calcular valor de mi inmueble
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>

              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600">
                <div className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-habita" /> Propiedades verificadas</div>
                <div className="flex items-center gap-2"><Award className="w-4 h-4 text-habita" /> Agentes certificados</div>
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-habita" /> Pago seguro</div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl bg-habita-mint">
                <img
                  src="https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=900&q=75"
                  srcSet="https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=600&q=75 600w, https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=900&q=75 900w, https://images.unsplash.com/photo-1505843513577-22bb7d21e455?auto=format&fit=crop&w=1200&q=75 1200w"
                  sizes="(max-width: 1024px) 0px, 480px"
                  alt="Casa moderna en Colombia"
                  width="480"
                  height="600"
                  fetchPriority="high"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl p-5 w-64">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <BadgeCheck className="w-3.5 h-3.5 text-emerald-700" />
                  </span>
                  <p className="text-xs text-gray-500 uppercase tracking-widest">100% Verificadas</p>
                </div>
                <p className="font-serif text-2xl font-bold text-habita-dark leading-tight">Inventario curado por nuestro equipo</p>
              </div>
              <div className="absolute -top-6 -right-6 bg-habita text-white rounded-2xl shadow-xl p-4 w-48 sm:w-52" data-testid="home-beta-badge">
                <p className="text-xs uppercase tracking-widest opacity-90">Beta</p>
                <p className="font-serif text-xl sm:text-2xl font-bold mt-1 leading-tight">Cobertura nacional 🇨🇴</p>
                <p className="text-xs opacity-90 mt-0.5 leading-snug">Disponible en los 1.122 municipios de Colombia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLATFORM SOCIAL PROOF — HIDDEN (Feb 2026): en esta etapa los números
          crudos del marketplace no fortalecen la confianza y pueden dar
          percepción de baja oferta. Se reactivará cuando escale.
          NOTA: código y datos se mantienen intactos para reactivación futura. */}
      {false && hasStats && (
        <PlatformStatsBar stats={stats} />
      )}

      {/* Bloque de valor diferencial — sustituye temporalmente al stats bar */}
      <HabitaValueProposition />

      {/* FEATURED LISTINGS */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20" data-testid="home-featured-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Destacadas esta semana</span>
            <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-habita-dark">Propiedades destacadas</h2>
            <p className="text-sm text-gray-500 mt-2 max-w-md">Publicaciones destacadas, verificadas y con más interés esta semana.</p>
          </div>
          <Link to="/buscar" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-habita hover:gap-2 transition-all">
            Ver todas <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {loadingFeat ? (
          <PropertyCardSkeletonGrid count={9} />
        ) : hasRealFeatured ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-featured-grid">
            {featured.map(p => <PropertyCard key={p.id} p={p} />)}
          </div>
        ) : (
          <EmptyState
            icon={HomeIcon}
            title="Todavía no hay propiedades destacadas"
            body="Sé el primero en publicar tu inmueble y aparece aquí sin costo durante nuestra beta."
            cta="Publicar mi propiedad"
            to="/publicar"
            testid="home-featured-empty"
          />
        )}
      </section>

      {/* RECENTLY POSTED */}
      {(loadingRecent || hasRealRecent) && (
        <section className="bg-habita-mint/40 py-16 lg:py-20" data-testid="home-recent-section">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-habita font-semibold">
                  <Clock className="w-3.5 h-3.5" /> Actualizado en tiempo real
                </span>
                <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-habita-dark">Recién publicadas</h2>
                <p className="text-sm text-gray-600 mt-2 max-w-md">Las últimas publicaciones de agentes e inmobiliarias en Habita.</p>
              </div>
              <Link to="/buscar?orden=recientes" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-habita hover:gap-2 transition-all">
                Ver todas <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            {loadingRecent ? (
              <PropertyCardSkeletonGrid count={8} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-recent-grid">
                {recent.map(p => <PropertyCard key={p.id} p={p} />)}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CONFIANZA */}
      <section className="bg-habita-dark text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-end mb-14">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">La diferencia habita</span>
              <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-white leading-tight">
                Confianza por <span className="italic text-amber-400">diseño</span>, no por accidente.
              </h2>
            </div>
            <p className="text-white/80 max-w-lg leading-relaxed">
              Detrás de cada publicación hay un proceso de verificación humano. Si algo no cuadra, lo eliminamos. Esa es nuestra promesa para esta beta.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: BadgeCheck, title: 'Propiedades verificadas', desc: 'Cada listado es revisado por nuestro equipo: fotos reales, dirección confirmada, precios coherentes con el mercado.' },
              { icon: Award, title: 'Agentes certificados', desc: 'Solo agentes e inmobiliarias verificadas pueden publicar directamente. El resto pasa por moderación.' },
              { icon: Flag, title: 'Reportes en un clic', desc: '¿Algo sospechoso? Repórtalo desde cada publicación. Revisamos cada caso en menos de 24 horas.' },
            ].map((b, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-amber-400/40 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-amber-400/15 text-amber-400 flex items-center justify-center">
                  <b.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl font-bold mt-4 text-white">{b.title}</h3>
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR AREAS (city discovery, no fake counters) */}
      <section className="bg-habita-cream py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Explora por ciudad</span>
              <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-habita-dark">Ciudades destacadas</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {POPULAR_AREAS.map(area => (
              <Link
                key={area.city}
                to={`/buscar?ciudad=${encodeURIComponent(area.city)}`}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden hover-lift"
                data-testid={`city-card-${area.city.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
              >
                <img src={area.image} alt={area.city} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="flex items-center gap-1 text-xs opacity-90"><MapPin className="w-3 h-3" /> Colombia</p>
                  <p className="font-serif font-bold text-2xl mt-0.5">{area.city}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-300 group-hover:text-amber-200">
                    Explorar propiedades <ChevronRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SELL CTA */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="relative bg-habita-dark rounded-3xl overflow-hidden p-10 lg:p-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative">
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Para propietarios</span>
            <h2 className="font-serif text-3xl lg:text-5xl font-bold text-white mt-3 leading-tight">
              ¿Quieres vender o arrendar tu propiedad?
            </h2>
            <p className="text-white/80 mt-5 leading-relaxed">
              Publica gratis, verificación humana, y llega a compradores en toda Colombia.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/publicar" className="btn-habita inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold bg-amber-500 hover:bg-amber-600 text-white">
                Publicar gratis <ChevronRight className="w-4 h-4" />
              </Link>
              <Link to="/calculadora" className="inline-flex items-center gap-2 px-7 h-12 rounded-full font-semibold border border-white/30 text-white hover:bg-white/10">
                Calcular hipoteca
              </Link>
            </div>
          </div>
          {hasStats ? (
            <div className="relative grid grid-cols-2 gap-4" data-testid="home-sell-cta-stats">
              {statPillsForCTA(stats).map((s, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/10">
                  <p className="font-serif text-3xl lg:text-4xl font-bold text-white tabular-nums">{s.value.toLocaleString('es-CO')}</p>
                  <p className="text-xs text-white/70 uppercase tracking-widest mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="relative bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10 text-center">
              <p className="font-serif text-2xl text-white mb-2">Beta abierta</p>
              <p className="text-sm text-white/70">Únete a los primeros propietarios y agentes que están construyendo el marketplace inmobiliario más confiable de Colombia.</p>
            </div>
          )}
        </div>
      </section>

      {/* NEWS */}
      {news.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16 lg:pb-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Prensa · noticias inmobiliarias</span>
              <h2 className="font-serif text-3xl lg:text-5xl font-bold mt-2 text-habita-dark">El mercado, en un solo lugar</h2>
            </div>
            <Link to="/prensa" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-habita hover:gap-2 transition-all">
              Ver todo <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {news.map(n => (
              <a key={n.id} href={n.url} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`home-news-${n.id}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100">
                  <img src={n.image} alt={n.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="mt-4">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="uppercase tracking-widest text-amber-600 font-semibold">{n.category}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-500 font-medium">{n.source}</span>
                  </div>
                  <h3 className="font-serif text-xl font-bold mt-1.5 text-habita-dark group-hover:text-habita transition-colors line-clamp-2">{n.title}</h3>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{n.excerpt}</p>
                  <p className="text-xs text-habita font-semibold mt-3 inline-flex items-center gap-1">
                    Leer en {n.source} <ChevronRight className="w-3 h-3" />
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}


/* ─────────────────────────────────────── Sub-components */

const STAT_LABELS = {
  active_properties: { label: 'Propiedades activas', icon: HomeIcon },
  agents:            { label: 'Agentes registrados', icon: Users },
  agencies:          { label: 'Inmobiliarias',       icon: Building2 },
  developers:        { label: 'Constructoras',       icon: HardHat },
  cities:            { label: 'Ciudades con inventario', icon: Globe2 },
  projects:          { label: 'Proyectos publicados', icon: Building },
  fresh_this_week:   { label: 'Publicadas esta semana', icon: Clock },
};

function PlatformStatsBar({ stats }) {
  // Sort keys by our preferred display order (props → agents → agencies → devs → cities → projects → fresh)
  const order = ['active_properties', 'agents', 'agencies', 'developers', 'cities', 'projects', 'fresh_this_week'];
  const entries = order.filter(k => stats[k]).map(k => ({ key: k, value: stats[k], ...STAT_LABELS[k] }));
  if (entries.length === 0) return null;
  return (
    <section className="bg-white border-b border-gray-100" data-testid="home-platform-stats">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {entries.map(e => (
            <div key={e.key} className="text-center" data-testid={`stat-${e.key}`}>
              <div className="w-10 h-10 mx-auto rounded-xl bg-habita-mint text-habita flex items-center justify-center mb-2">
                <e.icon className="w-5 h-5" />
              </div>
              <p className="font-serif text-2xl lg:text-3xl font-bold text-habita-dark tabular-nums">{e.value.toLocaleString('es-CO')}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">{e.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function statPillsForCTA(stats) {
  // Prefer the 4 highest-signal metrics for the sell-CTA (real data only)
  const priority = ['active_properties', 'agents', 'agencies', 'cities', 'projects', 'fresh_this_week'];
  const out = [];
  for (const k of priority) {
    if (!stats[k]) continue;
    out.push({ label: STAT_LABELS[k].label, value: stats[k] });
    if (out.length === 4) break;
  }
  return out;
}

function EmptyState({ icon: Icon, title, body, cta, to, testid }) {
  return (
    <div className="bg-habita-mint/40 border border-habita/10 rounded-3xl p-10 text-center" data-testid={testid}>
      <div className="w-14 h-14 mx-auto rounded-2xl bg-habita text-white flex items-center justify-center mb-4">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-serif text-2xl font-bold text-habita-dark">{title}</h3>
      <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">{body}</p>
      <Link to={to} className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-habita text-white font-semibold hover:bg-habita-dark">
        {cta} <ChevronRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
